'use client';

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { ActivityFeed, ActivityOperations } from '@/components/activities';
import clsx from 'clsx';
import { CirclePlus, Settings } from 'lucide-react';
import { Item } from '@/lib/api';
import LazyDisplay from './LazyDisplay';
import { LinkBrand } from './LinkBrand';
import { LinkItem } from './links/LinkItem';
import { appCreateContext } from '@/lib/context';
import { useSession } from '@/lib/auth-client';
import { FernmarkBadge } from '@/components/badges/FernmarkBadge';
import { extractTagValue } from '@/lib/utils';
import { useModalHelpers } from '@/lib/ui';
import { FollowButton } from './FollowButton';
import { EditItemDialog } from './items/EditItemDialog';
import { ImageGallery } from '@/components/ui/image-gallery';
import { useCountries } from '@/lib/hooks/useCountries';
import { ObjectConnections } from '@/components/ObjectConnections';
export const [ItemDetailContext, useItemDetailContext] = appCreateContext<{
  item: Item;
  onAddToCollection?: (itemId: string) => void;
}>();
interface ItemDetailProps {
  item: Item;
  onAddToCollection?: (itemId: string) => void;
  showTags?: boolean;
  showPrice?: boolean;
  showAddToCollection?: boolean;
}
export function ItemDetail(t0) {
  const $ = _c(105);
  const {
    item,
    onAddToCollection,
    showAddToCollection: t1,
    showPrice: t2
  } = t0;
  const showAddToCollection = t1 === undefined ? true : t1;
  t2 === undefined ? false : t2;
  const [selectedImage, setSelectedImage] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(!item.image);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const {
    data: sessionData
  } = useSession();
  useModalHelpers();
  let t3;
  if ($[0] !== item.brandPermissions?.permissions || $[1] !== sessionData?.user?.isAdmin) {
    t3 = sessionData?.user?.isAdmin || item.brandPermissions?.permissions.includes("edit_products") || false;
    $[0] = item.brandPermissions?.permissions;
    $[1] = sessionData?.user?.isAdmin;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const canEditProduct = t3;
  let t4;
  if ($[3] !== item.tags) {
    t4 = item.tags?.filter(_temp) || [];
    $[3] = item.tags;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const tagsToShow = t4;
  let t5;
  if ($[5] !== item) {
    t5 = (item as any).data?.gallery?.images as string[] || [];
    $[5] = item;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const galleryImages = t5;
  const {
    data: t6
  } = useCountries();
  let t7;
  if ($[7] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const countries = t7;
  const itemData = (item as any).data || {};
  const countryOrigin = itemData.country_origin as string | undefined;
  const countryManufactured = itemData.country_manufactured as string | undefined;
  const countriesIngredients = itemData.countries_ingredients as string[] | undefined || [];
  const hasCountryInfo = countryOrigin || countryManufactured || countriesIngredients.length > 0;
  let t8;
  if ($[9] !== countries) {
    t8 = new Map(countries.map(_temp2));
    $[9] = countries;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  const map = t8;
  let t9;
  if ($[11] !== map) {
    t9 = code => map.get(code) ?? code;
    $[11] = map;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  const resolveCountryName = t9;
  let t10;
  if ($[13] !== galleryImages) {
    t10 = galleryImages.map(_temp3);
    $[13] = galleryImages;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  const galleryItems = t10;
  const getPossessiveForm = _temp4;
  const imageRef = useRef(null);
  let t11;
  if ($[15] !== item.image) {
    t11 = [item.image];
    $[15] = item.image;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  const images = t11;
  let t12;
  if ($[17] !== item.image) {
    t12 = () => {
      setImageLoaded(false);
      setImageError(!item.image);
      if (imageRef.current?.complete) {
        setImageLoaded(true);
      }
    };
    $[17] = item.image;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== item.image || $[20] !== selectedImage) {
    t13 = [selectedImage, item.image];
    $[19] = item.image;
    $[20] = selectedImage;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  useEffect(t12, t13);
  const t14 = ItemDetailContext;
  let t15;
  if ($[22] !== item || $[23] !== onAddToCollection) {
    t15 = {
      item,
      onAddToCollection
    };
    $[22] = item;
    $[23] = onAddToCollection;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  const t16 = "";
  const t17 = "grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12";
  let t18;
  if ($[25] !== imageError || $[26] !== imageLoaded || $[27] !== images || $[28] !== item.title || $[29] !== selectedImage) {
    t18 = imageError ? <div className="aspect-square bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center"><div className="text-center p-8"><svg className="w-16 h-16 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg><p className="text-sm text-gray-500">Image unavailable</p></div></div> : <><div className={clsx("bg-gradient-to-br from-gray-100 to-gray-200 transition-opacity duration-300", imageLoaded ? "opacity-0 absolute inset-0" : "opacity-100")}><div className="aspect-square animate-pulse" /></div><LazyDisplay><img ref={imageRef} src={images[selectedImage] || ""} alt={item.title} className={clsx("max-h-[400] w-full border-solid object-contain transition-opacity duration-300", imageLoaded ? "opacity-100" : "opacity-0")} style={{
          aspectRatio: "1/1"
        }} onLoad={() => setImageLoaded(true)} onError={() => setImageError(true)} /></LazyDisplay></>;
    $[25] = imageError;
    $[26] = imageLoaded;
    $[27] = images;
    $[28] = item.title;
    $[29] = selectedImage;
    $[30] = t18;
  } else {
    t18 = $[30];
  }
  let t19;
  if ($[31] !== item.isNew) {
    t19 = item.isNew && <span className="absolute top-4 left-4 bg-emerald-500 text-white text-sm font-semibold px-3 py-1.5 rounded-full shadow-lg">NEW</span>;
    $[31] = item.isNew;
    $[32] = t19;
  } else {
    t19 = $[32];
  }
  let t20;
  if ($[33] !== t18 || $[34] !== t19) {
    t20 = <div className="relative bg-white rounded-2xl overflow-hidden">{t18}{t19}</div>;
    $[33] = t18;
    $[34] = t19;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== images || $[37] !== item.title || $[38] !== selectedImage) {
    t21 = images.length > 1 && <div className="grid grid-cols-4 gap-2">{images.map((image, index) => <button key={index} onClick={() => setSelectedImage(index)} className={clsx("relative aspect-square rounded-lg overflow-hidden border-2 transition-all", selectedImage === index ? "border-primary-500 ring-2 ring-primary-200" : "border-gray-200 hover:border-gray-300")}><LazyDisplay><img src={image || ""} alt={`${item.title} ${index + 1}`} className="w-full h-full object-cover" /></LazyDisplay></button>)}</div>;
    $[36] = images;
    $[37] = item.title;
    $[38] = selectedImage;
    $[39] = t21;
  } else {
    t21 = $[39];
  }
  let t22;
  if ($[40] !== t20 || $[41] !== t21) {
    t22 = <div className="space-y-4">{t20}{t21}</div>;
    $[40] = t20;
    $[41] = t21;
    $[42] = t22;
  } else {
    t22 = $[42];
  }
  const t23 = "flex flex-col gap-4 relative";
  let t24;
  if ($[43] !== canEditProduct) {
    t24 = canEditProduct && <div className="absolute top-0 right-0 text-gray-400 cursor-pointer hover:text-gray-600 z-10"><Settings onClick={() => setIsEditDialogOpen(true)} className="w-5 h-5" /></div>;
    $[43] = canEditProduct;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  const t25 = Number(item.id);
  let t26;
  if ($[45] !== item.title) {
    t26 = <h1 className="text-4xl font-bold text-gray-900">{item.title}</h1>;
    $[45] = item.title;
    $[46] = t26;
  } else {
    t26 = $[46];
  }
  let t27;
  if ($[47] !== t25 || $[48] !== t26) {
    t27 = <LinkItem itemId={t25} className="hover:opacity-80 transition-opacity">{t26}</LinkItem>;
    $[47] = t25;
    $[48] = t26;
    $[49] = t27;
  } else {
    t27 = $[49];
  }
  let t28;
  if ($[50] !== item.brand?.id || $[51] !== item.brand?.name_1) {
    t28 = item.brand?.name_1 && <LinkBrand className="text-lg text-gray-600 hover:underline mt-1" brandId={item.brand?.id}>{item.brand?.name_1}</LinkBrand>;
    $[50] = item.brand?.id;
    $[51] = item.brand?.name_1;
    $[52] = t28;
  } else {
    t28 = $[52];
  }
  let t29;
  if ($[53] !== t27 || $[54] !== t28) {
    t29 = <div>{t27}{t28}</div>;
    $[53] = t27;
    $[54] = t28;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== item.following || $[57] !== item.id || $[58] !== sessionData?.user?.approved) {
    t30 = sessionData?.user?.approved && item.following !== undefined && <FollowButton objectId={Number(item.id)} objectType="item" following={item.following} showLabel={true} />;
    $[56] = item.following;
    $[57] = item.id;
    $[58] = sessionData?.user?.approved;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  let t31;
  if ($[60] !== item.id || $[61] !== onAddToCollection || $[62] !== showAddToCollection) {
    t31 = showAddToCollection && <button onClick={() => onAddToCollection?.(item.id)} className="flex items-center gap-1.5 px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-all cursor-pointer text-sm font-medium" title="Add to Collection"><CirclePlus className="w-4 h-4" />Add to Collection</button>;
    $[60] = item.id;
    $[61] = onAddToCollection;
    $[62] = showAddToCollection;
    $[63] = t31;
  } else {
    t31 = $[63];
  }
  let t32;
  if ($[64] !== t30 || $[65] !== t31) {
    t32 = <div className="flex items-center gap-2 flex-wrap">{t30}{t31}</div>;
    $[64] = t30;
    $[65] = t31;
    $[66] = t32;
  } else {
    t32 = $[66];
  }
  let t33;
  if ($[67] !== item.brand || $[68] !== item.url) {
    t33 = item.url && <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-gray-900 text-gray-900 font-semibold rounded-xl hover:bg-gray-900 hover:text-white transition-all shadow-sm hover:shadow-md cursor-pointer"><span>View on {item.brand?.name_1 ? getPossessiveForm(item.brand.name_1) : "brand"}{" "}site</span><svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg></a>;
    $[67] = item.brand;
    $[68] = item.url;
    $[69] = t33;
  } else {
    t33 = $[69];
  }
  let t34;
  if ($[70] !== tagsToShow) {
    t34 = tagsToShow.length > 0 && <div className="flex flex-wrap gap-2 mt-3">{tagsToShow.map(_temp5)}</div>;
    $[70] = tagsToShow;
    $[71] = t34;
  } else {
    t34 = $[71];
  }
  let t35;
  if ($[72] !== t33 || $[73] !== t34) {
    t35 = <div className="space-y-6">{t33}{t34}</div>;
    $[72] = t33;
    $[73] = t34;
    $[74] = t35;
  } else {
    t35 = $[74];
  }
  let t36;
  if ($[75] !== item.description) {
    t36 = item.description && <div><p className="text-gray-700 leading-relaxed whitespace-pre-wrap">{item.description}</p></div>;
    $[75] = item.description;
    $[76] = t36;
  } else {
    t36 = $[76];
  }
  const t37 = hasCountryInfo && <div className="flex flex-col gap-y-1 text-sm text-gray-600">{countryOrigin && <span><span className="font-medium text-gray-700 w-40 inline-block">Country of Origin</span>{" "}{resolveCountryName(countryOrigin)}</span>}{countryManufactured && <span><span className="font-medium text-gray-700 w-40 inline-block">Made in</span>{" "}{resolveCountryName(countryManufactured)}</span>}{countriesIngredients.length > 0 && <span><span className="font-medium text-gray-700 w-40 inline-block">Ingredient Origins</span>{" "}{countriesIngredients.map(resolveCountryName).join(", ")}</span>}</div>;
  let t38;
  if ($[77] !== showAddToCollection) {
    t38 = showAddToCollection && <AddToCollection />;
    $[77] = showAddToCollection;
    $[78] = t38;
  } else {
    t38 = $[78];
  }
  let t39;
  if ($[79] !== t24 || $[80] !== t29 || $[81] !== t32 || $[82] !== t35 || $[83] !== t36 || $[84] !== t37 || $[85] !== t38) {
    t39 = <div className={t23}>{t24}{t29}{t32}{t35}{t36}{t37}{t38}</div>;
    $[79] = t24;
    $[80] = t29;
    $[81] = t32;
    $[82] = t35;
    $[83] = t36;
    $[84] = t37;
    $[85] = t38;
    $[86] = t39;
  } else {
    t39 = $[86];
  }
  let t40;
  if ($[87] !== t22 || $[88] !== t39) {
    t40 = <div className={t16}><div className={t17}>{t22}{t39}</div></div>;
    $[87] = t22;
    $[88] = t39;
    $[89] = t40;
  } else {
    t40 = $[89];
  }
  const t41 = Number(item.id);
  let t42;
  if ($[90] !== t41) {
    t42 = <ObjectConnections fromType="item" fromId={t41} toType="entity" title="Available At" showRelationships={false} className="mt-8" />;
    $[90] = t41;
    $[91] = t42;
  } else {
    t42 = $[91];
  }
  let t43;
  if ($[92] !== galleryItems) {
    t43 = galleryItems.length > 0 && <div className="mt-12"><ImageGallery items={galleryItems} className="rounded-2xl overflow-hidden" /></div>;
    $[92] = galleryItems;
    $[93] = t43;
  } else {
    t43 = $[93];
  }
  let t44;
  if ($[94] === Symbol.for("react.memo_cache_sentinel")) {
    t44 = () => setIsEditDialogOpen(false);
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  let t45;
  if ($[95] !== isEditDialogOpen || $[96] !== item) {
    t45 = <EditItemDialog isOpen={isEditDialogOpen} onClose={t44} item={item} />;
    $[95] = isEditDialogOpen;
    $[96] = item;
    $[97] = t45;
  } else {
    t45 = $[97];
  }
  let t46;
  if ($[98] !== t14.Provider || $[99] !== t15 || $[100] !== t40 || $[101] !== t42 || $[102] !== t43 || $[103] !== t45) {
    t46 = <t14.Provider value={t15}>{t40}{t42}{t43}{t45}</t14.Provider>;
    $[98] = t14.Provider;
    $[99] = t15;
    $[100] = t40;
    $[101] = t42;
    $[102] = t43;
    $[103] = t45;
    $[104] = t46;
  } else {
    t46 = $[104];
  }
  return t46;
}
function _temp5(tag_0) {
  return <Link key={tag_0} href={`/items?tags=${encodeURIComponent(tag_0)}`} className="bg-gray-100 text-gray-700 text-sm px-3 py-1 rounded-full hover:bg-gray-200 transition-colors cursor-pointer">{tag_0}</Link>;
}
function _temp4(name) {
  if (!name) {
    return "";
  }
  if (name.includes("'")) {
    return name;
  }
  return name.endsWith("s") ? `${name}'` : `${name}'s`;
}
function _temp3(url) {
  return {
    original: url,
    thumbnail: url
  };
}
function _temp2(c) {
  return [c.alpha2, c.name];
}
function _temp(tag) {
  return !tag.startsWith("@");
}
const AddToCollection = () => {
  const $ = _c(7);
  const {
    item,
    onAddToCollection
  } = useItemDetailContext();
  let t0;
  if ($[0] !== item.id || $[1] !== onAddToCollection) {
    t0 = () => onAddToCollection?.(item.id);
    $[0] = item.id;
    $[1] = onAddToCollection;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  let t1;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const t2 = item.userCollections?.length > 0 && `(${item.userCollections.length})`;
  let t3;
  if ($[4] !== t0 || $[5] !== t2) {
    t3 = <div className="space-y-3 pt-4"><button onClick={t0} className="w-full bg-primary-500 text-white font-semibold py-4 px-6 rounded-xl hover:bg-primary-600 transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2 cursor-pointer">{t1}Add to Collection {t2}</button></div>;
    $[4] = t0;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};